import { useLingui } from "@lingui/react/macro";
import type { IMedicalCompetence } from "@medoma/models/shifts";
import { Text } from "@medoma/components/Text/Text";
import type { DaySummaryData } from "@/api/Capacity";
import styles from "./DaySummaryPanel.module.scss";
import type { ReactNode } from "react";
import { CompetenceChip } from "@/components/Chips/CompetenceChip";
import { orderedCompetences } from "@medoma/models/shifts";
import { CategoryIcon } from "@/components/CategoryIcon/CategoryIcon";
import { activityCategoryDictionary } from "@/api/Activities";
import { isEmployeePerformedCategory } from "@medoma/models/activities";
import { calculateIncludedShiftsCount } from "../capacityOverviewUtils";

const SummaryHeading = ({ label }: { label: string }) => (
  <Text element="span" size="small" weight="bold">
    {label}
  </Text>
);

const SummaryBox = ({
  icon,
  label,
  count,
  additionalInfo,
}: {
  icon?: ReactNode;
  label: string;
  count: number;
  additionalInfo?: string;
}) => (
  <li className={styles.box}>
    <div className={styles.content}>
      {icon ? <div className={styles.icon}>{icon}</div> : null}
      <Text
        element="span"
        size="small"
        className={icon ? undefined : styles.labelWithoutIcon}
      >
        {label}
      </Text>
      <div className={styles.metrics}>
        <Text element="span" className={styles.boxCount}>
          {count}
        </Text>
        {additionalInfo ? (
          <Text
            element="span"
            size="smallest"
            color="faded"
            className={styles.additionalInfo}
          >
            {additionalInfo}
          </Text>
        ) : null}
      </div>
    </div>
  </li>
);

const CompetenceTag = ({
  competence,
  count,
}: {
  competence: IMedicalCompetence;
  count: number;
}) => (
  <li className={styles.competenceTag}>
    <CompetenceChip competence={competence} state="neutral" size="small" />
    <div className={styles.competenceTagCount}>
      <Text element="span" size="small">
        {count}
      </Text>
    </div>
  </li>
);

export const DaySummaryPanel = ({
  daySummaryData,
}: {
  daySummaryData: DaySummaryData;
}) => {
  const { t } = useLingui();

  const {
    admittedPatientsCount,
    assessmentPatientsCount,
    externalPatientsCount,
    plannedAdmissionPatientsCount,
    plannedDischargePatientsCount,
    patientsWithVisitsCount,
    categoryCounts,
    competenceCounts,
  } = daySummaryData;

  const sortedCompetenceCounts = [...competenceCounts].sort(
    (a, b) =>
      orderedCompetences.indexOf(a.competence) -
      orderedCompetences.indexOf(b.competence),
  );

  const competencesWithShifts = sortedCompetenceCounts
    .map(({ competence, shiftsByType }) => ({
      competence,
      includedShiftsCount: calculateIncludedShiftsCount(shiftsByType),
    }))
    .filter(({ includedShiftsCount }) => includedShiftsCount > 0);

  return (
    <div className={styles.panel}>
      <div className={styles.summary}>
        <SummaryHeading label={t`Patienter`} />

        <ul className={styles.group}>
          <SummaryBox
            label={t`Inskrivna`}
            count={admittedPatientsCount}
            additionalInfo={t`Varav ${patientsWithVisitsCount} med besök`}
          />
          <SummaryBox label={t`Bedömning`} count={assessmentPatientsCount} />
          {externalPatientsCount > 0 ? (
            <SummaryBox
              label={t`Särskilt uppdrag`}
              count={externalPatientsCount}
            />
          ) : null}

          <SummaryBox
            label={t`Planerad inskrivning`}
            count={plannedAdmissionPatientsCount}
          />
          <SummaryBox
            label={t`Planerad utskrivning`}
            count={plannedDischargePatientsCount}
          />
        </ul>
      </div>
      <div className={styles.summary}>
        <SummaryHeading label={t`Aktiviteter`} />

        <ul className={styles.group}>
          {categoryCounts
            // Patient-performed activities do not consume employee capacity
            .filter(({ category }) => isEmployeePerformedCategory(category))
            .map(({ category, count }) => (
              <SummaryBox
                key={category}
                icon={<CategoryIcon category={category} size="tiny" />}
                label={t(activityCategoryDictionary[category])}
                count={count}
              />
            ))}
        </ul>
      </div>
      <div className={styles.summary}>
        <SummaryHeading label={t`Arbetspass`} />

        {competencesWithShifts.length > 0 ? (
          <ul className={styles.competenceGrid}>
            {competencesWithShifts.map(
              ({ competence, includedShiftsCount }) => (
                <CompetenceTag
                  key={competence}
                  competence={competence}
                  count={includedShiftsCount}
                />
              ),
            )}
          </ul>
        ) : (
          <Text element="span" size="small" color="no-results">
            {t`Inga arbetspass`}
          </Text>
        )}
      </div>
    </div>
  );
};
